import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '../../lib/supabase';
import { extractBrandName, getWorkspaceBrandName } from '../../lib/brandName';
import { LLM_MODELS, getLlmMeta } from '../../lib/llmModels';
import { mergeResponseLinks, parseProviderLinks } from '../../lib/providerLinks';
import { SearchResponseModal, type SearchModalMentionStatus, type SearchResponseModalItem } from '../../components/SearchResponseModal';
import { useAuthStore } from '../../stores/authStore';
import type { Competitor, PromptResponse, PromptResponseCitation, PromptResponseMention, PromptRun, TrackedPrompt } from '../../types/database';

type PresetKey = 'today' | 'last7' | 'last30' | 'last90' | 'last365' | 'custom' | 'all';

type SearchRow = Pick<
  PromptResponse,
  | 'id'
  | 'prompt_id'
  | 'prompt_text_snapshot'
  | 'run_id'
  | 'model'
  | 'created_at'
  | 'response_text'
  | 'brand_mentioned'
  | 'competitors_mentioned'
  | 'citations'
  | 'provider_links'
>;

type MentionRow = Pick<
  PromptResponseMention,
  | 'prompt_response_id'
  | 'run_id'
  | 'brand_domain'
  | 'mentioned'
>;

type CitationRow = Pick<
  PromptResponseCitation,
  | 'prompt_response_id'
  | 'run_id'
  | 'citation_url'
  | 'citation_domain'
  | 'associated_brand_domains'
>;

type PromptLite = Pick<TrackedPrompt, 'id' | 'text'>;
type CompetitorLite = Pick<Competitor, 'name' | 'domain' | 'favicon_url'>;
type PromptRunLite = Pick<PromptRun, 'id' | 'status' | 'completed_at' | 'created_at'>;

type BrandMeta = {
  domain: string;
  name: string;
  faviconUrl: string;
  isWorkspaceBrand: boolean;
};

type SearchItem = SearchResponseModalItem & {
  promptId: string | null;
  previewText: string;
};

const PRESETS: { key: PresetKey; label: string }[] = [
  { key: 'today', label: 'Today' },
  { key: 'last7', label: 'Last 7 days' },
  { key: 'last30', label: 'Last 30 days' },
  { key: 'last90', label: 'Last 3 months' },
  { key: 'last365', label: 'Last 12 months' },
  { key: 'custom', label: 'Custom' },
  { key: 'all', label: 'All time' },
];

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DAY_HEADERS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];

const normalizeDomain = (value: string): string =>
  value
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, '')
    .replace(/^www\./, '')
    .replace(/\/+$/, '')
    .split('/')[0];

const domainMatches = (inputDomain: string, targetDomain: string): boolean => {
  const input = normalizeDomain(inputDomain);
  const target = normalizeDomain(targetDomain);
  return input === target || input.endsWith(`.${target}`);
};

const toStringArray = (value: unknown): string[] => {
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is string => typeof item === 'string');
};

const getHistoricalPromptText = (
  promptId: string | null | undefined,
  promptTextSnapshot: string | null | undefined,
  promptById: Map<string, string>
) => {
  if (promptId) {
    const livePromptText = promptById.get(promptId);
    if (livePromptText) return livePromptText;
  }

  if (promptTextSnapshot?.trim()) return promptTextSnapshot;
  return 'Prompt deleted';
};

const formatDate = (value: string): string =>
  new Date(value).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

const formatDateShort = (date: Date) =>
  `${date.getDate()} ${MONTH_NAMES[date.getMonth()].slice(0, 3)} ${date.getFullYear()}`;

const isWithinDateRange = (
  value: string,
  rangeStart: Date | null,
  rangeEnd: Date | null
): boolean => {
  const timestamp = new Date(value).getTime();
  if (Number.isNaN(timestamp)) return false;

  if (rangeStart) {
    const start = new Date(rangeStart.getFullYear(), rangeStart.getMonth(), rangeStart.getDate()).getTime();
    if (timestamp < start) return false;
  }

  if (rangeEnd) {
    const end = new Date(rangeEnd.getFullYear(), rangeEnd.getMonth(), rangeEnd.getDate(), 23, 59, 59, 999).getTime();
    if (timestamp > end) return false;
  }

  return true;
};

const getDaysInMonth = (year: number, month: number) =>
  new Date(year, month + 1, 0).getDate();

const getFirstDayOfWeek = (year: number, month: number) => {
  const day = new Date(year, month, 1).getDay();
  return day === 0 ? 6 : day - 1;
};

const isSameDay = (a: Date | null, b: Date | null) => {
  if (!a || !b) return false;
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
};

const isBetween = (date: Date, start: Date | null, end: Date | null) => {
  if (!start || !end) return false;
  const t = date.getTime();
  return t > start.getTime() && t < end.getTime();
};

const stripRichText = (value: string) =>
  value
    .replace(/!\[([^\]]*)\]\(([^)]+)\)/g, '$1 ')
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '$1')
    .replace(/[`*_>#-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const buildPreview = (value: string) => {
  const cleaned = stripRichText(value);
  if (cleaned.length <= 180) return cleaned;
  return `${cleaned.slice(0, 180).trimEnd()}...`;
};

function Calendar({
  year,
  month,
  rangeStart,
  rangeEnd,
  onDayClick,
  onPrevMonth,
  onNextMonth,
  showPrevNav,
  showNextNav,
}: {
  year: number;
  month: number;
  rangeStart: Date | null;
  rangeEnd: Date | null;
  onDayClick: (date: Date) => void;
  onPrevMonth: () => void;
  onNextMonth: () => void;
  showPrevNav: boolean;
  showNextNav: boolean;
}) {
  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfWeek(year, month);
  const today = new Date();
  const prevMonth = month === 0 ? 11 : month - 1;
  const prevYear = month === 0 ? year - 1 : year;
  const daysInPrevMonth = getDaysInMonth(prevYear, prevMonth);
  const cells: { day: number; isOtherMonth: boolean; date: Date }[] = [];

  for (let i = firstDay - 1; i >= 0; i--) {
    const day = daysInPrevMonth - i;
    cells.push({ day, isOtherMonth: true, date: new Date(prevYear, prevMonth, day) });
  }

  for (let day = 1; day <= daysInMonth; day++) {
    cells.push({ day, isOtherMonth: false, date: new Date(year, month, day) });
  }

  const remaining = 7 - (cells.length % 7);
  if (remaining < 7) {
    const nextMonth = month === 11 ? 0 : month + 1;
    const nextYear = month === 11 ? year + 1 : year;
    for (let day = 1; day <= remaining; day++) {
      cells.push({ day, isOtherMonth: true, date: new Date(nextYear, nextMonth, day) });
    }
  }

  return (
    <div className="date-range-calendar">
      <div className="calendar-header">
        <button className={`calendar-nav-btn ${!showPrevNav ? 'is-hidden' : ''}`} onClick={onPrevMonth} type="button">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="m15 18-6-6 6-6" />
          </svg>
        </button>
        <div className="calendar-month-title">
          {MONTH_NAMES[month]} {year}
        </div>
        <button className={`calendar-nav-btn ${!showNextNav ? 'is-hidden' : ''}`} onClick={onNextMonth} type="button">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="m9 18 6-6-6-6" />
          </svg>
        </button>
      </div>
      <div className="calendar-grid">
        {DAY_HEADERS.map((day) => (
          <div key={day} className="calendar-day-header">{day}</div>
        ))}
        {cells.map((cell, cellIndex) => {
          const isSelected = isSameDay(cell.date, rangeStart) || isSameDay(cell.date, rangeEnd);
          const isInRange = isBetween(cell.date, rangeStart, rangeEnd);
          const isToday = isSameDay(cell.date, today) && !cell.isOtherMonth;
          const isFuture = cell.date > today;

          let className = 'calendar-day';
          if (cell.isOtherMonth) className += ' is-other-month';
          if (isFuture) className += ' is-disabled';
          if (isSelected) className += ' is-selected';
          if (isInRange && !cell.isOtherMonth) className += ' is-in-range';
          if (isSelected && isSameDay(cell.date, rangeStart) && rangeEnd) className += ' is-range-start';
          if (isSelected && isSameDay(cell.date, rangeEnd) && rangeStart) className += ' is-range-end';
          if (isToday) className += ' is-today';

          return (
            <button
              key={cellIndex}
              className={className}
              onClick={() => !isFuture && !cell.isOtherMonth && onDayClick(cell.date)}
              type="button"
              disabled={isFuture}
            >
              {cell.day}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function Searches() {
  const { workspace } = useAuthStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const searchIdParam = searchParams.get('search');
  const brandParam = searchParams.get('brand');
  const promptParam = searchParams.get('prompt');
  const [selectedBrandDomain, setSelectedBrandDomain] = useState<string>(
    brandParam ? normalizeDomain(brandParam) : 'all'
  );
  const [isBrandDropdownOpen, setIsBrandDropdownOpen] = useState(false);
  const [selectedPromptId, setSelectedPromptId] = useState<string>(promptParam || 'all');
  const [isPromptDropdownOpen, setIsPromptDropdownOpen] = useState(false);
  const [selectedModelId, setSelectedModelId] = useState<string>('all');
  const [isModelDropdownOpen, setIsModelDropdownOpen] = useState(false);

  const [activePreset, setActivePreset] = useState<PresetKey>('today');
  const [appliedPreset, setAppliedPreset] = useState<PresetKey>('today');
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [datePickerVisible, setDatePickerVisible] = useState(false);
  const [selectedSearchId, setSelectedSearchId] = useState<string | null>(searchIdParam);
  const datePickerRef = useRef<HTMLDivElement>(null);
  const brandDropdownRef = useRef<HTMLDivElement>(null);
  const promptDropdownRef = useRef<HTMLDivElement>(null);
  const modelDropdownRef = useRef<HTMLDivElement>(null);

  const today = new Date();
  const [rangeStart, setRangeStart] = useState<Date | null>(
    new Date(today.getTime() - 24 * 60 * 60 * 1000)
  );
  const [rangeEnd, setRangeEnd] = useState<Date | null>(today);
  const [tempStart, setTempStart] = useState<Date | null>(rangeStart);
  const [tempEnd, setTempEnd] = useState<Date | null>(rangeEnd);
  const [pickingStart, setPickingStart] = useState(true);
  const [leftMonth, setLeftMonth] = useState(today.getMonth() === 0 ? 11 : today.getMonth() - 1);
  const [leftYear, setLeftYear] = useState(today.getMonth() === 0 ? today.getFullYear() - 1 : today.getFullYear());

  const rightMonth = leftMonth === 11 ? 0 : leftMonth + 1;
  const rightYear = leftMonth === 11 ? leftYear + 1 : leftYear;

  const openDatePicker = useCallback(() => {
    setTempStart(rangeStart);
    setTempEnd(rangeEnd);
    setActivePreset(appliedPreset);
    setPickingStart(true);
    setDatePickerVisible(true);
    requestAnimationFrame(() => {
      setIsDatePickerOpen(true);
    });
  }, [appliedPreset, rangeEnd, rangeStart]);

  const closeDatePicker = useCallback(() => {
    setIsDatePickerOpen(false);
    setTimeout(() => setDatePickerVisible(false), 120);
  }, []);

  const dismissDatePicker = useCallback(() => {
    setTempStart(rangeStart);
    setTempEnd(rangeEnd);
    setActivePreset(appliedPreset);
    setPickingStart(true);
    closeDatePicker();
  }, [appliedPreset, closeDatePicker, rangeEnd, rangeStart]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (datePickerRef.current && !datePickerRef.current.contains(event.target as Node)) {
        dismissDatePicker();
      }

      if (brandDropdownRef.current && !brandDropdownRef.current.contains(event.target as Node)) {
        setIsBrandDropdownOpen(false);
      }

      if (promptDropdownRef.current && !promptDropdownRef.current.contains(event.target as Node)) {
        setIsPromptDropdownOpen(false);
      }

      if (modelDropdownRef.current && !modelDropdownRef.current.contains(event.target as Node)) {
        setIsModelDropdownOpen(false);
      }
    };

    if (datePickerVisible || isBrandDropdownOpen || isPromptDropdownOpen || isModelDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [datePickerVisible, dismissDatePicker, isBrandDropdownOpen, isModelDropdownOpen, isPromptDropdownOpen]);

  useEffect(() => {
    setSelectedSearchId(searchIdParam);
  }, [searchIdParam]);

  useEffect(() => {
    setSelectedBrandDomain(brandParam ? normalizeDomain(brandParam) : 'all');
  }, [brandParam]);

  useEffect(() => {
    setSelectedPromptId(promptParam || 'all');
  }, [promptParam]);

  useEffect(() => {
    if (!selectedSearchId) return;
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        const params = new URLSearchParams(searchParams);
        params.delete('search');
        setSearchParams(params, { replace: true });
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [searchParams, selectedSearchId, setSearchParams]);

  const handlePresetClick = (key: PresetKey) => {
    setActivePreset(key);
    const now = new Date();
    let start: Date | null = null;
    let end: Date | null = now;

    if (key === 'today') start = new Date(now.getTime() - 24 * 60 * 60 * 1000);
    else if (key === 'last7') start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 6);
    else if (key === 'last30') start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 27);
    else if (key === 'last90') start = new Date(now.getFullYear(), now.getMonth() - 3, now.getDate());
    else if (key === 'last365') start = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
    else if (key === 'all') {
      start = null;
      end = null;
    } else if (key === 'custom') {
      return;
    }

    setTempStart(start);
    setTempEnd(end);
    setRangeStart(start);
    setRangeEnd(end);
    setAppliedPreset(key);
    closeDatePicker();
  };

  const handleDayClick = (date: Date) => {
    setActivePreset('custom');
    if (pickingStart) {
      setTempStart(date);
      setTempEnd(null);
      setPickingStart(false);
    } else {
      if (tempStart && date < tempStart) {
        setTempEnd(tempStart);
        setTempStart(date);
      } else {
        setTempEnd(date);
      }
      setPickingStart(true);
    }
  };

  const handleApply = () => {
    setAppliedPreset(activePreset);
    setRangeStart(tempStart);
    setRangeEnd(tempEnd);
    closeDatePicker();
  };

  const handleCancel = () => dismissDatePicker();

  const handlePrevMonth = () => {
    if (leftMonth === 0) {
      setLeftMonth(11);
      setLeftYear(leftYear - 1);
    } else {
      setLeftMonth(leftMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (leftMonth === 11) {
      setLeftMonth(0);
      setLeftYear(leftYear + 1);
    } else {
      setLeftMonth(leftMonth + 1);
    }
  };

  const dateLabel = (() => {
    const applied = PRESETS.find((item) => item.key === appliedPreset);
    if (appliedPreset !== 'custom') return applied?.label || 'Today';
    if (rangeStart && rangeEnd) return `${formatDateShort(rangeStart)} – ${formatDateShort(rangeEnd)}`;
    return 'Custom';
  })();

  const { data: prompts = [] } = useQuery({
    queryKey: ['tracked_prompts', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return [];
      const { data, error } = await supabase
        .from('tracked_prompts')
        .select('id, text')
        .eq('workspace_id', workspace.id);

      if (error) throw error;
      return data as PromptLite[];
    },
    enabled: !!workspace?.id,
  });

  const { data: competitors = [] } = useQuery({
    queryKey: ['searches_competitors', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return [];
      const { data, error } = await supabase
        .from('competitors')
        .select('name, domain, favicon_url')
        .eq('workspace_id', workspace.id)
        .order('created_at', { ascending: true });

      if (error) throw error;
      return data as CompetitorLite[];
    },
    enabled: !!workspace?.id,
  });

  const { data: promptResponses = [] } = useQuery({
    queryKey: ['searches_prompt_responses', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return [];
      const { data, error } = await supabase
        .from('prompt_responses')
        .select('id, prompt_id, prompt_text_snapshot, run_id, model, created_at, response_text, brand_mentioned, competitors_mentioned, citations, provider_links')
        .eq('workspace_id', workspace.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as SearchRow[];
    },
    enabled: !!workspace?.id,
  });

  const { data: promptRuns = [] } = useQuery({
    queryKey: ['searches_prompt_runs', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return [];
      const { data, error } = await supabase
        .from('prompt_runs')
        .select('id, status, completed_at, created_at')
        .eq('workspace_id', workspace.id)
        .eq('status', 'completed')
        .order('completed_at', { ascending: false });

      if (error) throw error;
      return data as PromptRunLite[];
    },
    enabled: !!workspace?.id,
  });

  const { data: mentionRows = [] } = useQuery({
    queryKey: ['searches_prompt_response_mentions', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return [];
      const { data, error } = await supabase
        .from('prompt_response_mentions')
        .select('prompt_response_id, run_id, brand_domain, mentioned')
        .eq('workspace_id', workspace.id)
        .eq('mentioned', true);

      if (error) {
        if (error.code === '42P01') return [];
        throw error;
      }

      return data as MentionRow[];
    },
    enabled: !!workspace?.id,
  });

  const { data: citationRows = [] } = useQuery({
    queryKey: ['searches_prompt_response_citations', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return [];
      const { data, error } = await supabase
        .from('prompt_response_citations')
        .select('prompt_response_id, run_id, citation_url, citation_domain, associated_brand_domains')
        .eq('workspace_id', workspace.id);

      if (error) {
        if (error.code === '42P01') return [];
        throw error;
      }

      return data as CitationRow[];
    },
    enabled: !!workspace?.id,
  });

  const promptById = useMemo(
    () => new Map(prompts.map((prompt) => [prompt.id, prompt.text])),
    [prompts]
  );

  const workspaceBrandDomain = workspace?.brand_url ? normalizeDomain(workspace.brand_url) : '';

  const brandByDomain = useMemo(() => {
    const map = new Map<string, BrandMeta>();

    if (workspaceBrandDomain) {
      map.set(workspaceBrandDomain, {
        domain: workspaceBrandDomain,
        name: getWorkspaceBrandName(workspace),
        faviconUrl: `https://www.google.com/s2/favicons?domain=${workspaceBrandDomain}&sz=64`,
        isWorkspaceBrand: true,
      });
    }

    competitors.forEach((competitor) => {
      const domain = normalizeDomain(competitor.domain);
      if (!domain) return;
      map.set(domain, {
        domain,
        name: competitor.name || extractBrandName(domain),
        faviconUrl: competitor.favicon_url || `https://www.google.com/s2/favicons?domain=${domain}&sz=64`,
        isWorkspaceBrand: false,
      });
    });

    return map;
  }, [competitors, workspace, workspaceBrandDomain]);

  const buildBrandsForResponse = useCallback((response: SearchRow): BrandMeta[] => {
    const items: BrandMeta[] = [];
    const seen = new Set<string>();

    if (response.brand_mentioned && workspaceBrandDomain && brandByDomain.has(workspaceBrandDomain)) {
      items.push(brandByDomain.get(workspaceBrandDomain)!);
      seen.add(workspaceBrandDomain);
    }

    if (Array.isArray(response.competitors_mentioned)) {
      for (const raw of response.competitors_mentioned as Array<Record<string, unknown>>) {
        const mentioned = raw.mentioned === true;
        const domain = typeof raw.domain === 'string' ? normalizeDomain(raw.domain) : '';
        if (!mentioned || !domain || seen.has(domain)) continue;

        if (brandByDomain.has(domain)) {
          items.push(brandByDomain.get(domain)!);
        } else {
          items.push({
            domain,
            name: extractBrandName(domain),
            faviconUrl: `https://www.google.com/s2/favicons?domain=${domain}&sz=64`,
            isWorkspaceBrand: false,
          });
        }
        seen.add(domain);
      }
    }

    return items;
  }, [brandByDomain, workspaceBrandDomain]);

  const mentionsByResponseId = useMemo(() => {
    const map = new Map<string, MentionRow[]>();
    mentionRows.forEach((row) => {
      const existing = map.get(row.prompt_response_id) || [];
      existing.push(row);
      map.set(row.prompt_response_id, existing);
    });
    return map;
  }, [mentionRows]);

  const citationsByResponseId = useMemo(() => {
    const map = new Map<string, CitationRow[]>();
    citationRows.forEach((row) => {
      const existing = map.get(row.prompt_response_id) || [];
      existing.push(row);
      map.set(row.prompt_response_id, existing);
    });
    return map;
  }, [citationRows]);

  const getMentionStatusForResponse = useCallback((response: SearchRow, brandDomain: string): SearchModalMentionStatus => {
    if (!brandDomain) return 'none';

    const normalizedBrandDomain = normalizeDomain(brandDomain);
    const responseMentions = mentionsByResponseId.get(response.id) || [];
    const responseCitations = citationsByResponseId.get(response.id) || [];

    const hasStructuredMention = responseMentions.some((row) => domainMatches(row.brand_domain || '', normalizedBrandDomain));
    const hasDirectCitation = responseCitations.some((row) => {
      const associatedDomains = toStringArray(row.associated_brand_domains).map(normalizeDomain);
      return associatedDomains.some((domain) => domainMatches(domain, normalizedBrandDomain))
        && domainMatches(row.citation_domain || '', normalizedBrandDomain);
    });
    const hasIndirectCitation = responseCitations.some((row) => {
      const associatedDomains = toStringArray(row.associated_brand_domains).map(normalizeDomain);
      return associatedDomains.some((domain) => domainMatches(domain, normalizedBrandDomain))
        && !domainMatches(row.citation_domain || '', normalizedBrandDomain);
    });

    if (hasDirectCitation) return 'mentioned';
    if (hasIndirectCitation && hasStructuredMention) return 'indirect';

    if (normalizedBrandDomain === workspaceBrandDomain && response.brand_mentioned) {
      return hasIndirectCitation ? 'indirect' : 'mentioned';
    }

    if (Array.isArray(response.competitors_mentioned)) {
      const legacyMention = (response.competitors_mentioned as Array<Record<string, unknown>>).some((raw) => {
        const mentioned = raw.mentioned === true;
        const domain = typeof raw.domain === 'string' ? normalizeDomain(raw.domain) : '';
        return mentioned && domainMatches(domain, normalizedBrandDomain);
      });

      if (legacyMention) {
        return hasIndirectCitation ? 'indirect' : 'mentioned';
      }
    }

    if (hasStructuredMention) {
      return hasIndirectCitation ? 'indirect' : 'mentioned';
    }

    return 'none';
  }, [citationsByResponseId, mentionsByResponseId, workspaceBrandDomain]);

  const effectiveStatusBrandDomain = selectedBrandDomain === 'all' ? workspaceBrandDomain : selectedBrandDomain;

  const allSearches = useMemo<SearchItem[]>(
    () =>
      promptResponses.map((response) => {
        const structuredCitations = citationsByResponseId.get(response.id) || [];
        const storedCitationValues = Array.isArray(response.citations)
          ? response.citations.filter((item): item is string => typeof item === 'string')
          : [];
        const citationMap = new Map<string, { url: string; domain: string }>();

        structuredCitations.forEach((citation) => {
          const url = typeof citation.citation_url === 'string' ? citation.citation_url : '';
          const domain = typeof citation.citation_domain === 'string' ? citation.citation_domain : normalizeDomain(url);
          if (!url || citationMap.has(url)) return;
          citationMap.set(url, { url, domain });
        });

        storedCitationValues.forEach((url) => {
          if (!url || citationMap.has(url)) return;
          citationMap.set(url, { url, domain: normalizeDomain(url) });
        });

        const citations = Array.from(citationMap.values());
        const responseLinks = mergeResponseLinks(citations, parseProviderLinks(response.provider_links));

        return {
          id: response.id,
          promptId: response.prompt_id,
          promptText: getHistoricalPromptText(response.prompt_id, response.prompt_text_snapshot, promptById),
          createdAt: response.created_at,
          model: response.model,
          responseText: response.response_text,
          previewText: buildPreview(response.response_text),
          brands: buildBrandsForResponse(response),
          citations,
          responseLinks,
          mentionStatus: effectiveStatusBrandDomain
            ? getMentionStatusForResponse(response, effectiveStatusBrandDomain)
            : 'none',
        };
      }),
    [buildBrandsForResponse, citationsByResponseId, effectiveStatusBrandDomain, getMentionStatusForResponse, promptById, promptResponses]
  );

  const brandOptions = useMemo(
    () => [
      { domain: 'all', name: 'Show all brands', faviconUrl: '', isWorkspaceBrand: false },
      ...Array.from(brandByDomain.values()),
    ],
    [brandByDomain]
  );

  const selectedBrand = useMemo(
    () => brandOptions.find((brand) => brand.domain === selectedBrandDomain) || brandOptions[0],
    [brandOptions, selectedBrandDomain]
  );

  const selectedBrandLabel = selectedBrandDomain === 'all' ? 'Filter by brand' : selectedBrand.name;

  const selectedPrompt = useMemo(
    () => prompts.find((prompt) => prompt.id === selectedPromptId) || null,
    [prompts, selectedPromptId]
  );

  const modelOptions = useMemo(
    () => [
      { id: 'all', name: 'All AI Platforms', short: 'AI', color: '#807a79', icon: '' },
      ...LLM_MODELS.map((model) => ({
        id: model.name,
        ...model,
      })),
    ],
    []
  );

  const selectedModel = useMemo(
    () => modelOptions.find((model) => model.id === selectedModelId) || modelOptions[0],
    [modelOptions, selectedModelId]
  );

  const latestCompletedRunId = promptRuns[0]?.id || null;

  const filteredSearches = useMemo(
    () =>
      allSearches.filter((search) => {
        if (appliedPreset === 'today' && latestCompletedRunId) {
          const matchingResponse = promptResponses.find((response) => response.id === search.id);
          if (!matchingResponse || matchingResponse.run_id !== latestCompletedRunId) return false;
        } else if (!isWithinDateRange(search.createdAt, rangeStart, rangeEnd)) {
          return false;
        }
        if (selectedPromptId !== 'all' && search.promptId !== selectedPromptId) return false;
        if (selectedModelId !== 'all' && search.model !== selectedModelId) return false;
        if (selectedBrandDomain === 'all') return true;
        return search.brands.some((brand) => brand.domain === selectedBrandDomain);
      }),
    [allSearches, appliedPreset, latestCompletedRunId, promptResponses, rangeEnd, rangeStart, selectedBrandDomain, selectedModelId, selectedPromptId]
  );

  const selectedSearch = useMemo(
    () => allSearches.find((search) => search.id === selectedSearchId) || null,
    [allSearches, selectedSearchId]
  );

  const openSearch = (searchId: string) => {
    const params = new URLSearchParams(searchParams);
    params.set('search', searchId);
    setSearchParams(params, { replace: true });
  };

  const closeSearch = () => {
    const params = new URLSearchParams(searchParams);
    params.delete('search');
    setSearchParams(params, { replace: true });
  };

  return (
    <div>
      <div className="spacer-xsmall"></div>

      <div className="overview-header" style={{ marginBottom: '1.5rem' }}>
        <div className="overview-header-left">
          <h1>Searches</h1>
        </div>

        <div className="searches-filter-row">
          <div className="date-range-wrapper search-brand-filter-wrapper" ref={brandDropdownRef}>
            <button
              type="button"
              className="date-range-toggle search-brand-filter-toggle"
              onClick={() => setIsBrandDropdownOpen((current) => !current)}
            >
              <div className="overview-brand-dropdown-value">
                {selectedBrand.faviconUrl ? (
                  <div
                    className="overview-brand-dropdown-icon"
                    style={{ backgroundImage: `url("${selectedBrand.faviconUrl}")` }}
                  />
                ) : (
                  <div className="search-brand-filter-placeholder">
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10" />
                      <path d="M2 12h20" />
                      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                    </svg>
                  </div>
                )}
                <div className="text-size-small text-weight-medium text-colour-dark-brown">{selectedBrandLabel}</div>
              </div>
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={isBrandDropdownOpen ? { transform: 'rotate(180deg)', transition: 'transform 0.2s' } : { transition: 'transform 0.2s' }}>
                <path d="m6 9 6 6 6-6" />
              </svg>
            </button>

            {isBrandDropdownOpen && (
              <div className="workspace-dropdown overview-brand-dropdown search-brand-dropdown">
                <div className="workspace-dropdown-list">
                  {brandOptions.map((brand) => (
                    <button
                      key={brand.domain}
                      type="button"
                      className={`workspace-dropdown-item ${selectedBrandDomain === brand.domain ? 'is-active' : ''}`}
                      onClick={() => {
                        setSelectedBrandDomain(brand.domain);
                        setIsBrandDropdownOpen(false);
                      }}
                    >
                      <div className="overview-brand-dropdown-item-main">
                        {brand.faviconUrl ? (
                          <div
                            className="overview-brand-dropdown-icon"
                            style={{ backgroundImage: `url("${brand.faviconUrl}")` }}
                          />
                        ) : (
                          <div className="search-brand-filter-placeholder is-small">
                            <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <circle cx="12" cy="12" r="10" />
                              <path d="M2 12h20" />
                              <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                            </svg>
                          </div>
                        )}
                        <div className="text-size-small text-colour-dark-brown">{brand.name}</div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="date-range-wrapper search-brand-filter-wrapper" ref={promptDropdownRef}>
            <button
              type="button"
              className="date-range-toggle search-brand-filter-toggle search-prompt-filter-toggle"
              onClick={() => setIsPromptDropdownOpen((current) => !current)}
            >
              <div className="overview-brand-dropdown-value">
                <div className="search-brand-filter-placeholder">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M8 6h13" />
                    <path d="M8 12h13" />
                    <path d="M8 18h13" />
                    <path d="M3 6h.01" />
                    <path d="M3 12h.01" />
                    <path d="M3 18h.01" />
                  </svg>
                </div>
                <div className="text-size-small text-weight-medium text-colour-dark-brown">
                  {selectedPromptId === 'all' ? 'All prompts' : selectedPrompt?.text || 'Prompt unavailable'}
                </div>
              </div>
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={isPromptDropdownOpen ? { transform: 'rotate(180deg)', transition: 'transform 0.2s' } : { transition: 'transform 0.2s' }}>
                <path d="m6 9 6 6 6-6" />
              </svg>
            </button>

            {isPromptDropdownOpen && (
              <div className="workspace-dropdown overview-brand-dropdown search-brand-dropdown search-prompt-dropdown">
                <div className="workspace-dropdown-list">
                  <button
                    type="button"
                    className={`workspace-dropdown-item ${selectedPromptId === 'all' ? 'is-active' : ''}`}
                    onClick={() => {
                      setSelectedPromptId('all');
                      setIsPromptDropdownOpen(false);
                    }}
                  >
                    <div className="overview-brand-dropdown-item-main">
                      <div className="search-brand-filter-placeholder is-small">
                        <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M8 6h13" />
                          <path d="M8 12h13" />
                          <path d="M8 18h13" />
                          <path d="M3 6h.01" />
                          <path d="M3 12h.01" />
                          <path d="M3 18h.01" />
                        </svg>
                      </div>
                      <div className="text-size-small text-colour-dark-brown">All prompts</div>
                    </div>
                  </button>
                  {prompts.map((prompt) => (
                    <button
                      key={prompt.id}
                      type="button"
                      className={`workspace-dropdown-item ${selectedPromptId === prompt.id ? 'is-active' : ''}`}
                      onClick={() => {
                        setSelectedPromptId(prompt.id);
                        setIsPromptDropdownOpen(false);
                      }}
                    >
                      <div className="text-size-small text-colour-dark-brown search-prompt-option-label">
                        {prompt.text}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="date-range-wrapper search-brand-filter-wrapper" ref={modelDropdownRef}>
            <button
              type="button"
              className="date-range-toggle search-brand-filter-toggle search-model-filter-toggle"
              onClick={() => setIsModelDropdownOpen((current) => !current)}
            >
              <div className="overview-brand-dropdown-value">
                {selectedModelId === 'all' ? (
                  <div className="search-brand-filter-placeholder">
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9L12 3z" />
                    </svg>
                  </div>
                ) : selectedModel.icon ? (
                  <div className="overview-model-badge">
                    <img src={selectedModel.icon} alt={selectedModel.name} className="overview-model-badge-image" />
                  </div>
                ) : (
                  <div className="overview-model-badge">{selectedModel.short}</div>
                )}
                <div className="text-size-small text-weight-medium text-colour-dark-brown">
                  {selectedModelId === 'all' ? 'All AI Platforms' : selectedModel.name}
                </div>
              </div>
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={isModelDropdownOpen ? { transform: 'rotate(180deg)', transition: 'transform 0.2s' } : { transition: 'transform 0.2s' }}>
                <path d="m6 9 6 6 6-6" />
              </svg>
            </button>

            {isModelDropdownOpen && (
              <div className="workspace-dropdown overview-brand-dropdown search-brand-dropdown search-model-dropdown">
                <div className="workspace-dropdown-list">
                  {modelOptions.map((model) => (
                    <button
                      key={model.id}
                      type="button"
                      className={`workspace-dropdown-item ${selectedModelId === model.id ? 'is-active' : ''}`}
                      onClick={() => {
                        setSelectedModelId(model.id);
                        setIsModelDropdownOpen(false);
                      }}
                    >
                      <div className="overview-brand-dropdown-item-main">
                        {model.id === 'all' ? (
                          <div className="search-brand-filter-placeholder is-small">
                            <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9L12 3z" />
                            </svg>
                          </div>
                        ) : model.icon ? (
                          <div className="overview-model-badge">
                            <img src={model.icon} alt={model.name} className="overview-model-badge-image" />
                          </div>
                        ) : (
                          <div className="overview-model-badge">{model.short}</div>
                        )}
                        <div className="text-size-small text-colour-dark-brown">
                          {model.id === 'all' ? 'All AI Platforms' : model.name}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="date-range-wrapper" ref={datePickerRef}>
            <div className="date-range-toggle" onClick={() => (datePickerVisible ? dismissDatePicker() : openDatePicker())}>
              <div className="date-range-selected">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M8 2v4" />
                  <path d="M16 2v4" />
                  <rect width="18" height="18" x="3" y="4" rx="2" />
                  <path d="M3 10h18" />
                </svg>
                <div className="text-size-small text-weight-medium text-colour-dark-brown">{dateLabel}</div>
              </div>
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={isDatePickerOpen ? { transform: 'rotate(180deg)', transition: 'transform 0.2s' } : { transition: 'transform 0.2s' }}>
                <path d="m6 9 6 6 6-6" />
              </svg>
            </div>

            {datePickerVisible && (
              <div className={`date-range-dropdown ${isDatePickerOpen ? 'is-visible' : ''}`}>
                <div className="date-range-presets">
                  {PRESETS.map((preset) => (
                    <button
                      key={preset.key}
                      className={`date-range-preset ${activePreset === preset.key ? 'is-active' : ''}`}
                      onClick={() => handlePresetClick(preset.key)}
                      type="button"
                    >
                      {preset.label}
                      {activePreset === preset.key && (
                        <span className="date-range-preset-check">
                          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M20 6 9 17l-5-5" />
                          </svg>
                        </span>
                      )}
                    </button>
                  ))}
                </div>
                <div style={{ flex: 1 }}>
                  <div className="date-range-calendars">
                    <Calendar
                      year={leftYear}
                      month={leftMonth}
                      rangeStart={tempStart}
                      rangeEnd={tempEnd}
                      onDayClick={handleDayClick}
                      onPrevMonth={handlePrevMonth}
                      onNextMonth={handleNextMonth}
                      showPrevNav={true}
                      showNextNav={false}
                    />
                    <Calendar
                      year={rightYear}
                      month={rightMonth}
                      rangeStart={tempStart}
                      rangeEnd={tempEnd}
                      onDayClick={handleDayClick}
                      onPrevMonth={handlePrevMonth}
                      onNextMonth={handleNextMonth}
                      showPrevNav={false}
                      showNextNav={true}
                    />
                  </div>
                  <div className="date-range-footer">
                    <button className="button is-clear" onClick={handleCancel} type="button">
                      Cancel
                    </button>
                    <button className="button is-apply" onClick={handleApply} disabled={activePreset === 'custom' && (!tempStart || !tempEnd)} type="button">
                      Apply
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {filteredSearches.length === 0 ? (
        <div className="dashboard-panel">
          <div className="dashboard-panel-body">
            <div className="overview-panel-empty">No searches found for this period</div>
          </div>
        </div>
      ) : (
        <div className="recent-search-grid searches-grid">
          {filteredSearches.map((item) => {
            const modelMeta = getLlmMeta(item.model);

            return (
              <div
                key={item.id}
                className="recent-search-card searches-card"
                role="button"
                tabIndex={0}
                onClick={() => openSearch(item.id)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    openSearch(item.id);
                  }
                }}
              >
                <div className="recent-search-card-main">
                  <div className="recent-search-meta">
                    <div className="recent-search-model">
                      <div className="overview-model-badge">
                        {modelMeta.icon ? (
                          <img src={modelMeta.icon} alt={modelMeta.name} className="overview-model-badge-image" />
                        ) : (
                          modelMeta.short
                        )}
                      </div>
                      <span>{modelMeta.name}</span>
                    </div>
                    <div className="recent-search-date">{formatDate(item.createdAt)}</div>
                  </div>

                  <div className="recent-search-prompt">{item.promptText}</div>

                  {effectiveStatusBrandDomain && (
                    <div className="search-card-status-row">
                      <div className={`search-mention-tag is-${item.mentionStatus}`}>
                        {item.mentionStatus === 'mentioned'
                          ? 'Mentioned'
                          : item.mentionStatus === 'indirect'
                            ? 'Indirect mention'
                            : 'No mention'}
                      </div>
                    </div>
                  )}

                  <div className="search-card-brands-row">
                    <div className="text-size-xsmall text-colour-alt-grey">Brands mentioned</div>
                    {item.brands.length === 0 ? (
                      <div className="text-size-xsmall text-colour-light-grey">—</div>
                    ) : (
                      <div className="search-brand-stack">
                        {item.brands.slice(0, 6).map((brand) => (
                          <div key={`${item.id}-${brand.domain}`} className="search-brand-badge info-tooltip-wrapper">
                            <div className="search-brand-badge-image" style={{ backgroundImage: `url(${brand.faviconUrl})` }} />
                            <span className="info-tooltip">{brand.name}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="search-card-preview">{item.previewText || 'No preview available'}</div>
                </div>

                <div className="search-card-footer">
                  <button type="button" className="button is-tiny is-white has-inner-shadow w-button">
                    See full answer
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <SearchResponseModal item={selectedSearch} onClose={closeSearch} />

      <div className="spacer-large"></div>
    </div>
  );
}
